import { useMemo, useState } from 'react';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import useSmallScreen from '../../../hooks/useSmallScreen';
import ColumnAvatar from '../../../components/Redesing/column-avatar';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';
import {
  useSystemsRelayQuery,
  mapRelaySystemsToRows,
  ISystemListRow,
  SystemQueryFilter,
  SystemSortInput,
} from '../../../hooks/system/query/V2';
import { useOrganizationsRelayQuery } from '../../../hooks/organization/query/V2';
import { history } from '../../../utils';
import AddIcon from '@mui/icons-material/Add';
import { SearchChip } from '../../../ts/interface/components/table.interface';
import { MovaticFieldGroupSelectFilter } from '../../../components/Redesing/movatic-field-group';
import { Chip, Box } from '@mui/material';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const sortFieldMap: Record<string, SystemSortInput['field']> = {
  name: 'NAME',
  status: 'STATUS',
};

function SystemTable({
  currentSystemId,
  pageState,
  systemClick,
  handleSearch,
  hasAccess,
  onClose,
  handleApplyFilter,
  handleDeleteFilter,
}: {
  currentSystemId: string;
  pageState: {
    search: string;
    selectedFilter: { label: string; value: string }[];
    filter: { label: string; value: string }[];
  };
  systemClick: (id: string) => void;
  handleSearch: (search: string) => void;
  hasAccess: boolean;
  onClose: () => void;
  handleApplyFilter: (newValues: { label: string; value: string }[]) => void;
  handleDeleteFilter: (filter: { label: string; value: string }[]) => void;
}) {
  const isSmallScreen = useSmallScreen();
  const [chips, setChips] = useState<SearchChip[]>(
    pageState.selectedFilter.map((filter) => ({
      label: 'Organizations',
      field: 'organizations',
      value: filter.value,
      displayValue: filter.label,
      disabled: false,
    }))
  );

  const debouncedSearchTerm = useSearchDebounce(pageState.search);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [pageNumber, setPageNumber] = useState(0);
  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [accumulatedSystems, setAccumulatedSystems] = useState<ISystemListRow[]>([]);

  const [sort, setSort] = useState<ISortConfig>({
    key: 'name',
    direction: 'asc',
  });

  const [selectedFilter, setSelectedFilter] = useState<
    { value: string; label: string }[]
  >(pageState.selectedFilter);

  const { data: organizations, isLoading: isOrganizationsLoading } =
    useOrganizationsRelayQuery(undefined, { limit: 100 });

  // The systems list is token-scoped server side (only systems the admin can
  // access come back), so the filter only carries the explicit UI state.
  const systemsFilter = useMemo(() => {
    const filter: SystemQueryFilter = {};
    if (debouncedSearchTerm) {
      filter.search = debouncedSearchTerm;
    }
    const organizationIds = chips.map((chip) => String(chip.value));
    if (organizationIds.length) {
      filter.organizationIds = organizationIds;
    }
    return filter;
  }, [debouncedSearchTerm, chips]);

  const graphqlSort: SystemSortInput[] = useMemo(() => {
    const field = sortFieldMap[sort.key];
    if (!field) return [{ field: 'NAME', order: 'ASC' }];
    return [{ field, order: sort.direction === 'asc' ? 'ASC' : 'DESC' }];
  }, [sort]);

  const {
    data: graphqlData,
    count: totalSystems,
    hasMore: graphqlHasMore,
    endCursor,
    isLoading,
    isRefetching,
  } = useSystemsRelayQuery(systemsFilter, {
    limit: 100,
    after: afterCursor,
    sort: graphqlSort,
  });

  const systemRows = useMemo(
    () => (graphqlData ? mapRelaySystemsToRows(graphqlData) : []),
    [graphqlData]
  );

  useMemo(() => {
    if (!systemRows.length && !afterCursor) {
      setAccumulatedSystems([]);
      return;
    }
    if (systemRows.length) {
      if (afterCursor) {
        setAccumulatedSystems((prev) => [...prev, ...systemRows]);
      } else {
        setAccumulatedSystems(systemRows);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemRows]);

  const systems = accumulatedSystems.length > 0 ? accumulatedSystems : systemRows;

  const resetPagination = () => {
    setAccumulatedSystems([]);
    setAfterCursor(null);
    setPageNumber(0);
  };

  const onApplyFilter = () => {
    if (selectedFilter.length > 0) {
      const newChips = selectedFilter.map((filter: { label: string; value: string }) => ({
        label: 'Organizations',
        field: 'organizations',
        value: filter.value,
        displayValue: filter.label,
        disabled: false,
      }));
      handleApplyFilter(selectedFilter);
      resetPagination();
      setChips(newChips);
    }
  };

  const handleChipDelete = (chip: SearchChip) => {
    const newChips = chips.filter((c) => c.label !== chip.label);
    // search on selectedFilter the chips.value that will deleted, chip.value is an array
    const filterToDelete = selectedFilter.find(
      (f) => Array.isArray(chip.value) && chip.value.includes(f.value)
    );
    if (filterToDelete) {
      handleDeleteFilter([filterToDelete]); // Wrap in an array
    }
    resetPagination();
    setChips(newChips);
    setSelectedFilter([]);
  };

  return (
    <PaginationTable
      padding={{
        pl: -1,
        pt: 1,
        pr: 1,
      }}
      totalItems={totalSystems}
      hasMore={graphqlHasMore || systems.length > (pageNumber + 1) * rowsPerPage}
      isLoading={
        isLoading ||
        isOrganizationsLoading ||
        (pageNumber > 0 && systems.length <= pageNumber * rowsPerPage)
      }
      isLoadingCache={isRefetching}
      customKey="gid"
      onRowCLick={(id: string) => systemClick(id)}
      handleSearch={(search) => {
        resetPagination();
        handleSearch(search.target.value);
      }}
      searchText={pageState.search}
      sortBy={sort.key}
      sortDirection={sort.direction}
      onSortChange={(sort) => {
        resetPagination();
        setSort(sort as ISortConfig);
      }}
      getSelectedRow={(row: ISystemListRow) => row.id === currentSystemId}
      onApplyFilter={onApplyFilter}
      handleChipDelete={handleChipDelete}
      chips={chips}
      spaceFilters={
        <>
          <MovaticFieldGroupSelectFilter
            dataId="system-select-org-filter"
            label="Organizations"
            value={selectedFilter}
            onChange={(event: React.SyntheticEvent, newValue: any[]) => {
              setSelectedFilter(newValue);
            }}
            options={
              organizations?.map((org: { id: string; name: string }) => ({
                value: org.id,
                label: org.name,
              })) || []
            }
          />
        </>
      }
      buttons={[
        {
          startIcon: <AddIcon />,
          variant: 'contained',
          label: 'Add',
          dataId: 'add-system',
          disabled: !hasAccess,
          onClick: () => {
            history.push('/addsystem');
            onClose();
          },
        },
      ]}
      columns={
        [
          ...(isSmallScreen
            ? [
                {
                  id: 'name',
                  label: 'System Name',
                  searchable: true,
                  maxWidth: 'none',
                  format: (row: ISystemListRow) => {
                    const getStatusChip = (status: string) => {
                      switch (status) {
                        case 'hibernation':
                          return (
                            <Chip
                              icon={<PauseCircleIcon />}
                              label="Hibernating"
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          );
                        case 'closed':
                          return (
                            <Chip
                              icon={<CancelIcon />}
                              label="Closed"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          );
                        case 'open':
                        default:
                          return (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Open"
                              color="success"
                              size="small"
                              variant="outlined"
                            />
                          );
                      }
                    };

                    return (
                      <ColumnAvatar
                        noEllipsis
                        avatar={<AccountTreeIcon />}
                        firstRow={row.name}
                        secondRow={getStatusChip(row.status)}
                      />
                    );
                  },
                },
              ]
            : [
                {
                  id: 'name',
                  label: 'System Name',
                  searchable: true,
                  showToolTip: true,
                  format: (row: ISystemListRow) => {
                    return (
                      <ColumnAvatar
                        noEllipsis
                        avatar={<AccountTreeIcon />}
                        firstRow={row.name}
                      />
                    );
                  },
                },
                {
                  id: 'status',
                  label: 'Status',
                  format: (row: ISystemListRow) => {
                    const getStatusChip = (status: string) => {
                      switch (status) {
                        case 'hibernation':
                          return (
                            <Chip
                              icon={<PauseCircleIcon />}
                              label="Hibernating"
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          );
                        case 'closed':
                          return (
                            <Chip
                              icon={<CancelIcon />}
                              label="Closed"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          );
                        case 'open':
                        default:
                          return (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Open"
                              color="success"
                              size="small"
                              variant="outlined"
                            />
                          );
                      }
                    };

                    return <Box>{getStatusChip(row.status)}</Box>;
                  },
                },
              ]),
          {
            id: 'partner_name',
            label: 'Organization',
            searchable: true,
            notSortable: true,
          },
        ] as IColumn[]
      }
      localSearch={false}
      items={systems || []}
      onPageChange={(page, newPage) => {
        const incrementedPage = newPage + 1;
        if (systems?.length && systems.length / rowsPerPage < incrementedPage) {
          setAfterCursor(endCursor || null);
        }
        setPageNumber(newPage);
      }}
      onRowsPerPageChange={(pageSizeString) => {
        const pageSize = Number(pageSizeString.target.value);
        resetPagination();
        setRowsPerPage(pageSize);
      }}
      page={pageNumber}
      rowsPerPage={rowsPerPage}
    />
  );
}

export default SystemTable;
